import { useId } from "react";
import type {
    Counterparty,
    FederalTaxClassification,
} from "@common/types/counterparty";
import {
    type CounterpartyTinIds,
    useCounterpartyTinQuery,
} from "@api/counterpartyTin.api";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
import { VStack } from "@design-system/VStack/VStack";
import { getVgsConfig, type VgsConfig } from "@lib/vgs/vgsConfig";
import { getDefaultTinType } from "./tinType";
import { TinEntryField } from "./TinEntryField";
import { TinSavedState } from "./TinSavedState";
import type { TinDraft } from "./useTinDraft";
import { useCanManageCounterpartyTin } from "./useCanManageCounterpartyTin";
import styles from "./CounterpartyTinSection.module.scss";

interface CounterpartyTinSectionProps {
    counterparty?: Counterparty;
    federalTaxClassification: FederalTaxClassification | null;
    draft: TinDraft;
    isSubmitting: boolean;
    onSubmitRequest: () => void;
}

type TinFieldProps = Omit<CounterpartyTinSectionProps, "counterparty"> & {
    config: VgsConfig;
};

export const CounterpartyTinSection = ({
    counterparty,
    ...fieldProps
}: CounterpartyTinSectionProps) => {
    const labelId = useId();
    const canManageTin = useCanManageCounterpartyTin();
    const config = getVgsConfig();

    if (!canManageTin) {
        return null;
    }

    const workspaceId = counterparty?.workspaceId;
    let body = (
        <span className={styles.message}>Tax ID storage isn't configured</span>
    );
    if (config && counterparty && workspaceId) {
        body = (
            <SavedCounterpartyTin
                ids={{ workspaceId, counterpartyId: counterparty.id }}
                config={config}
                {...fieldProps}
            />
        );
    } else if (config) {
        body = (
            <TinEntryField
                config={config}
                defaultTinType={getDefaultTinType(
                    fieldProps.federalTaxClassification,
                )}
                draft={fieldProps.draft}
                isSubmitting={fieldProps.isSubmitting}
                onSubmitRequest={fieldProps.onSubmitRequest}
            />
        );
    }

    return (
        <VStack gap={8} role="group" aria-labelledby={labelId}>
            <span id={labelId} className={styles.label}>
                Tax ID
            </span>
            {body}
        </VStack>
    );
};

const SavedCounterpartyTin = ({
    ids,
    config,
    federalTaxClassification,
    draft,
    isSubmitting,
    onSubmitRequest,
}: TinFieldProps & { ids: CounterpartyTinIds }) => {
    const tinQuery = useCounterpartyTinQuery({ ids, enabled: true });

    if (tinQuery.isPending) {
        return <Skeleton height={32} />;
    }
    if (tinQuery.isError) {
        return <span className={styles.message}>Failed to load tax ID</span>;
    }

    const savedTinType = tinQuery.data.tinType;
    if (savedTinType && !draft.isReplacing) {
        return (
            <TinSavedState
                ids={ids}
                config={config}
                tinType={savedTinType}
                tinRevisionId={tinQuery.data.tinRevisionId}
                reviews={tinQuery.data.reviews}
                onUpdate={draft.startReplacing}
            />
        );
    }

    return (
        <TinEntryField
            config={config}
            defaultTinType={
                savedTinType ?? getDefaultTinType(federalTaxClassification)
            }
            draft={draft}
            isSubmitting={isSubmitting}
            onSubmitRequest={onSubmitRequest}
            onCancel={savedTinType ? draft.cancelReplacing : undefined}
        />
    );
};
